import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'
import { getSmartPlatform } from '@weathernews/btos-javascript-modules/src/wniutil/common.js'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const isMobile = ref(false)

const moveLayersAbove = (map) => {
  const style = map.getStyle()
  const layers = style.layers
  const labelLayers = layers.filter(
    (layer) =>
      layer.id.includes('label')
      || layer.id.includes('text')
      || layer.id.includes('place')
      || layer.id.includes('train-station')
      || layer.id.includes('prefline')
      || layer.id.includes('Highway')
      || layer.type === 'symbol'
      || layer.id === 'Other border'
  )

  labelLayers.forEach((layer) => {
    map.moveLayer(layer.id)
  })
}

export const useKosaMap = (zoomLevel, baseMapType, bgOpacity, echoOpacity, layerMaxZoom) => {
  const config = useRuntimeConfig()
  const isConfigOpen = ref(false)
  const markerPosition = useCookie('markerPosition', { default: () => null })
  const tmpMarkerPosition = ref({})
  const currentTileUrl = ref(null)
  if (import.meta.client) {
    isMobile.value = getSmartPlatform() !== ''
  }
  const baseMaps = {
    'osm-basic': `${config.public.mapDomain}/style/service/mobile/onebox/radar/basic.json`,
    'gsi-photo': `${config.public.mapDomain}/style/service/mobile/onebox/radar/satellite.json`
  }
  const japanBounds = [
    [112.0, 10.0],
    [164.0, 55.0]
  ]
  const loadedMap = shallowRef(null)
  let customLocationControl = null

  const addLocationMarker = async (map) => {
    if (!map.hasImage('user-location-img')) {
      const image = await map.loadImage(`${config.public.imgDomain}/onebox/radar/img/icon/icon_location_pin.png`)
      map.addImage('user-location-img', image.data)
    }
    if (!map.getSource('crosshair-vertical')) {
      map.addSource('crosshair-vertical', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      })
    }

    if (!map.getSource('crosshair-horizontal')) {
      map.addSource('crosshair-horizontal', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      })
    }

    if (map.getSource('crosshair-vertical') && !map.getLayer('crosshair-vertical-line')) {
      map.addLayer({
        id: 'crosshair-vertical-line',
        type: 'line',
        source: 'crosshair-vertical',
        paint: {
          'line-color': '#ffffff',
          'line-width': 1,
          'line-opacity': 0.8
        }
      })
    }

    if (map.getSource('crosshair-horizontal') && !map.getLayer('crosshair-horizontal-line')) {
      map.addLayer({
        id: 'crosshair-horizontal-line',
        type: 'line',
        source: 'crosshair-horizontal',
        paint: {
          'line-color': '#ffffff',
          'line-width': 1,
          'line-opacity': 0.8
        }
      })
    }
    if (!map.getSource('user-location')) {
      map.addSource('user-location', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [] },
              properties: {}
            }
          ]
        }
      })
    }
    if (map.getSource('user-location') && !map.getLayer('user-location-symbol')) {
      map.addLayer({
        id: 'user-location-symbol',
        type: 'symbol',
        source: 'user-location',
        layout: {
          'icon-image': 'user-location-img',
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            3,
            0.25,
            11,
            0.4
          ],
          'icon-anchor': 'bottom',
          'icon-offset': [0, 6],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        }
      })
    }
  }

  const addKosaLayer = async (map, tileUrl) => {
    currentTileUrl.value = tileUrl
    if (!map.getSource('kosa-tiles')) {
      map.addSource('kosa-tiles', {
        type: 'raster',
        tiles: [`${tileUrl}/{z}_{x}_{y}.png`],
        tileSize: 256,
        bounds: [
          118.006250,
          20.004167,
          149.993750,
          47.995833
        ],
        maxzoom: layerMaxZoom
      })
    }

    if (map.getSource('kosa-tiles') && !map.getLayer('kosa-tiles')) {
      map.addLayer({
        id: 'kosa-tiles',
        type: 'raster',
        source: 'kosa-tiles',
        paint: {
          'raster-opacity': echoOpacity.value,
          'raster-resampling': 'nearest'
        }
      })
    }
  }

  const addAllLayer = async (map, tileUrl) => {
    map.addLayer({
      id: 'gray-overlay',
      type: 'background',
      paint: {
        'background-color': '#000',
        'background-opacity': bgOpacity.value
      }
    })
    await Promise.all([
      addLocationMarker(map),
      addKosaLayer(map, tileUrl)
    ])
    moveLayersAbove(map)
    if (markerPosition.value) {
      updateCrosshair(map, markerPosition.value.lat, markerPosition.value.lon)
      tmpMarkerPosition.value = { lat: markerPosition.value.lat, lon: markerPosition.value.lon }
      markerPosition.value = null
    }
  }

  const updateKosaTile = (tileUrl) => {
    currentTileUrl.value = tileUrl
    if (loadedMap.value?.getSource('kosa-tiles')) {
      loadedMap.value.getSource('kosa-tiles').setTiles([`${tileUrl}/{z}_{x}_{y}.png`])
    }
  }

  const updateCrosshair = (map, lat, lon) => {
    if (map.getSource('user-location')) {
      map.getSource('user-location').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lon, lat] }
          }
        ]
      })
    }
    if (map.getSource('crosshair-vertical')) {
      map.getSource('crosshair-vertical').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [
              [lon, japanBounds[0][1]],
              [lon, japanBounds[1][1]]
            ] }
          }
        ]
      })
    }
    if (map.getSource('crosshair-horizontal')) {
      map.getSource('crosshair-horizontal').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [
              [japanBounds[0][0], lat],
              [japanBounds[1][0], lat]
            ] }
          }
        ]
      })
    }
  }

  watch(baseMapType, (newType) => {
    if (loadedMap.value) {
      loadedMap.value.setStyle(baseMaps[newType])
      loadedMap.value.once('style.load', async () => {
        await addAllLayer(loadedMap.value, currentTileUrl.value)
      })
    }
  })

  const initMap = (mapContainer, mapCenter, bounds, firstTileUrl) => {
    const position = useCookie('mapView', { default: () => null })
    mapCenter.value = position.value ? [position.value.lon, position.value.lat] : mapCenter.value
    return new Promise((resolve) => {
      let attributionControl = null
      const protocol = new Protocol()
      maplibregl.addProtocol('pmtiles', protocol.tile)
      const mapOptions = {
        container: mapContainer,
        style: baseMaps[baseMapType.value],
        localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
        center: mapCenter.value,
        zoom: position.value ? position.value.zoom : zoomLevel.value,
        minZoom: 3,
        maxZoom: 14,
        maxBounds: japanBounds,
        language: 'ja',
        attributionControl: false,
        dragRotate: false,
        keyboard: false,
        touchPitch: false
      }
      if (!position.value) {
        mapOptions.bounds = Array.isArray(bounds) && bounds.length > 0 ? bounds : japanBounds
      }
      const map = new maplibregl.Map(mapOptions)
      map.touchZoomRotate.disableRotation()

      map.once('style.load', async () => {
        attributionControl = new CustomAttributionControl({
          compact: true
        })
        map.addControl(attributionControl, 'bottom-left')
        map.addControl(new CustomMapConfigControl(isConfigOpen), 'bottom-left')
        customLocationControl = new CustomLocationControl(japanBounds, tmpMarkerPosition)
        map.addControl(customLocationControl, 'bottom-left')
        map.addControl(new maplibregl.NavigationControl({
          showCompass: false,
          showZoom: true
        }), 'bottom-left')

        map.on('moveend', () => {
          mapCenter.value = [map.getCenter().lng, map.getCenter().lat]
          zoomLevel.value = map.getZoom()
        })

        let isTapped = null
        let touchMoved = false

        const handleTapEvent = async (e) => {
          if (isTapped) {
            // ダブルタップ（300ms以内）が行われた場合タイムアウトを解除しピンを打つなどの処理を行わない
            clearTimeout(isTapped)
            isTapped = null
            touchMoved = false
            return
          }
          isTapped = setTimeout(async () => {
            if (!touchMoved) {
              // シングルタップの処理：ピンを立てる
              updateCrosshair(map, e.lngLat.lat, e.lngLat.lng)
              tmpMarkerPosition.value = { lat: e.lngLat.lat, lon: e.lngLat.lng }
              if (customLocationControl) {
                customLocationControl.deactivate()
              }
            }
            isTapped = null
            touchMoved = false
          }, 300)
        }

        map.on('touchmove', () => {
          touchMoved = true
        })
        if (isMobile.value) {
          map.on('touchend', handleTapEvent)
        } else {
          map.on('click', handleTapEvent)
        }

        await addAllLayer(map, firstTileUrl)
        position.value = null
        loadedMap.value = map
        resolve()
      })
    })
  }
  return {
    initMap,
    updateKosaTile,
    loadedMap,
    tmpMarkerPosition,
    isConfigOpen
  }
}
